<!-- eslint-disable no-undef -->
<script lang="ts" setup>
import {
  onMounted, ref, watch, nextTick,
} from 'vue';

const props = defineProps<{ modelValue: string; placeholder?: string }>();
const emit = defineEmits(['update:modelValue']);

const editorRef = ref<HTMLDivElement | null>(null);
const internalHtml = ref(props.modelValue || '');

watch(() => props.modelValue, (val) => {
  const next = val || '';
  if (next !== internalHtml.value) internalHtml.value = next;
  if (editorRef.value && editorRef.value.innerHTML !== next) {
    editorRef.value.innerHTML = next;
  }
});

function exec(command: 'bold') {
  document.execCommand(command);
}

function formatAs(block: 'p' | 'h1' | 'h2') {
  if (!editorRef.value) return;
  document.execCommand('formatBlock', false, block);
}

function onInput() {
  internalHtml.value = editorRef.value?.innerHTML || '';
  emit('update:modelValue', internalHtml.value);
}

onMounted(async () => {
  await nextTick();
  if (editorRef.value && internalHtml.value) {
    editorRef.value.innerHTML = internalHtml.value;
  }
});
</script>

<template>
  <div class="rounded-xl border border-zinc-700 bg-[#0C0C0C]">
    <div class="flex items-center gap-2 px-3 py-2 border-b border-zinc-800 bg-[#0C0C0C] sticky top-0 z-10">
      <button class="px-3 py-1.5 rounded-lg text-zinc-200 bg-[#181818] hover:bg-[#202020] border border-zinc-700" @click="formatAs('h1')">H1</button>
      <button class="px-3 py-1.5 rounded-lg text-zinc-200 bg-[#181818] hover:bg-[#202020] border border-zinc-700" @click="formatAs('h2')">H2</button>
      <button class="px-3 py-1.5 rounded-lg text-zinc-200 bg-[#181818] hover:bg-[#202020] border border-zinc-700" @click="formatAs('p')">Text</button>
      <div class="w-px h-6 bg-zinc-800 mx-1" />
      <button class="px-3 py-1.5 rounded-lg text-black bg-gold-primary hover:bg-gold-secondary border border-gold-secondary" @click="exec('bold')"><span class="font-semibold">B</span></button>
    </div>
    <div
      ref="editorRef"
      class="px-4 py-4 min-h-[320px] outline-none prose prose-invert max-w-none focus:shadow-inner"
      contenteditable="true"
      :placeholder="placeholder || 'Write a beautiful note…'"
      @input="onInput"
    />
  </div>
</template>

<style scoped>
[contenteditable][placeholder]:empty:before {
  content: attr(placeholder);
  color: #71717a;
}
.prose :where(h1):not(:where(.not-prose *)) {
  font-size: 1.875rem; /* text-3xl */
  line-height: 2.25rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}
.prose :where(h2):not(:where(.not-prose *)) {
  font-size: 1.5rem; /* text-2xl */
  line-height: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.prose :where(p):not(:where(.not-prose *)) {
  color: rgba(255,255,255,0.92);
}
</style>
